import { getAuth } from 'firebase/auth';
import type { Appointment, AppointmentBriefing } from '@researcher/shared';
import { apiUrl } from '../config/api';

async function getHeaders(): Promise<HeadersInit> {
  const auth = getAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export async function getAppointments(status?: string): Promise<Appointment[]> {
  const headers = await getHeaders();
  const params = new URLSearchParams();
  if (status) params.set('status', status);

  const response = await fetch(`${apiUrl('/appointments')}?${params.toString()}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch appointments');
  }

  const data = await response.json();
  return data.data;
}

export async function createAppointment(appointment: Partial<Appointment>): Promise<Appointment> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/appointments'), {
    method: 'POST',
    headers,
    body: JSON.stringify(appointment),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create appointment');
  }

  const data = await response.json();
  return data.data;
}

export async function updateAppointment(id: string, updates: Partial<Appointment>): Promise<Appointment> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/appointments/${id}`), {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to update appointment');
  }

  const data = await response.json();
  return data.data;
}

export async function deleteAppointment(id: string): Promise<void> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/appointments/${id}`), {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to delete appointment');
  }
}

export async function generateBriefing(id: string): Promise<AppointmentBriefing> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/appointments/${id}/briefing`), {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to generate briefing');
  }

  const data = await response.json();
  return data.data;
}
